import { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Popover,
  SvgIconProps,
  Stack,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useLayoutComponentEditorStore } from './useLayoutComponentEditorStore';
import { ILayoutComponentEditorProps } from './LayoutComponentEditor.types';
import { RenderDynamicHook, useSubEditorProps } from './Support';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import {
  ILayoutComponent,
  useDialogs,
  useLayoutComponentSettings,
  LayoutComponentNames,
  ILayoutComponentSettings,
  IProperties,
  mutateLayoutComponentLabel,
  useFlowBuilder2Store,
  hash,
  useAssistantLoadingStore,
  getLayoutComponentLabel,
  useEditorStore,
  useNotifications,
  useFlowBuilder2ChangeLayoutComponentMutation,
  getResponseErrorMessage,
  useHiddenComponent,
} from '@diana/core';
import { LAYOUT_COMPONENTS_NS } from '../../../../../../i18n/layoutComponents';
import { useFlowBuilder2PageContext } from '../../../../Context/useFlowBuilder2PageContext';
import { useFlowBuilder2PageStore } from '../../../../Store/useFlowBuilder2PageStore';
import { BuilderConfigureEditorTabEnum } from '../../../../Store/FlowBuilder2PageStore.types';
import { ConfigureDrawerHeader } from '../../ConfigureDrawer.styles';
import { RightDrawerHeaderSwitcher } from '../../../../Components/RightDrawer/RightDrawerHeaderSwitcher';
import {
  IRightDrawerAction,
  RightDrawerActions,
} from '../../../../Components/RightDrawer/RightDrawerActions';
import { useDeleteComponent } from '../../../../Support/useDeleteComponent';
import { useDuplicateComponent } from '../../../../Support/useDuplicateComponent';

interface IHeaderProps {
  layoutId: string;
  value: ILayoutComponent;
  title: ReactNode;
  onClose?: () => void;
  isSubEditor?: boolean;
  CloseIcon?: FunctionComponent<SvgIconProps>;
  onChange: (layoutComponent: ILayoutComponent) => void;
}

const Header = (props: IHeaderProps) => {
  const { layoutId, title, onClose, CloseIcon, value, onChange, isSubEditor = false } = props;
  const { t } = useTranslation(LAYOUT_COMPONENTS_NS);
  const { palette } = useTheme();

  const [changeTypeAnchorEl, setChangeTypeAnchorEl] = useState<Element | null>(null);

  const { confirm } = useDialogs();
  const { isLoading: isPageLoading } = useFlowBuilder2PageContext();
  const { getAllSettings, getSettings } = useLayoutComponentSettings();

  const notifications = useNotifications();

  const flowResourceType = useEditorStore((s) => s.flow?.properties?.flowResourceType);
  const updateLayoutComponentById = useEditorStore((s) => s.updateLayoutComponentById);

  const isLinked = useFlowBuilder2Store((s) => !!s.getComponentSourceId(value.id));

  const activeComponent = useFlowBuilder2PageStore((store) => store.activeComponent);
  const activeTab = useFlowBuilder2PageStore((store) => store.rightDrawerActiveTab);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const disabled = hasAnyPendingStatus || isChatLoading;
  const isSection = value.component === LayoutComponentNames.SECTION;
  const isConfigurationTab = activeTab === BuilderConfigureEditorTabEnum.Configuration;
  const settings = useMemo(() => getSettings(value), [value, getSettings]);

  const { deleteComponent } = useDeleteComponent();
  const { duplicateComponent } = useDuplicateComponent();
  const {
    canHide: canHideComponent,
    onChange: onHideChange,
    isHidden,
  } = useHiddenComponent({
    value,
    onChange,
  });

  const showHideAction = !!settings?.showHideAction && canHideComponent;

  const { mutate: change, isPending: isChanging } = useFlowBuilder2ChangeLayoutComponentMutation({
    onSuccess: (data) => {
      updateLayoutComponentById(layoutId, data.id, data);
    },
    onError: (error) => {
      notifications.error({
        message: t('errors.componentError'),
        description: getResponseErrorMessage(error),
      });
    },
  });

  const onDelete = useCallback(() => {
    if (value && activeComponent) {
      const { layoutId, componentId } = activeComponent;
      deleteComponent({ layoutId, componentId }).catch();
    }
  }, [value, activeComponent, deleteComponent]);

  const onDuplicate = useCallback(() => {
    if (value && activeComponent) {
      const { layoutId, componentId } = activeComponent;
      duplicateComponent({ layoutId, componentId }).catch();
    }
  }, [value, activeComponent, duplicateComponent]);

  const changeType = useCallback(
    async (component: LayoutComponentNames, componentConfig: ILayoutComponentSettings) => {
      if (isLinked) {
        const confirmed = await confirm(null, {
          variant: 'warning',
          confirmButtonLabel: t('actions.proceed'),
          title: t('descriptions.changeComponentTypeWarning'),
          description: t('descriptions.changeComponentTypeNote'),
        });
        if (!confirmed) return;
      }
      setChangeTypeAnchorEl(null);
      if (value.component !== component) {
        change({
          layoutId,
          componentId: value.id,
          componentType: component,
        });
      } else {
        onChange(
          produce(value, (draft) => {
            draft.component = component;
            draft.properties = {
              ...draft.properties,
              ...componentConfig.matchProperties,
            } as IProperties;
            mutateLayoutComponentLabel(draft, getLayoutComponentLabel(draft));
          }),
        );
      }
    },
    [t, onChange, confirm, value, setChangeTypeAnchorEl, isLinked, change, layoutId],
  );

  const interchangeableComponents = useMemo(() => {
    return getAllSettings().filter(([component, config]) => {
      return (
        !config.hidden &&
        flowResourceType &&
        config.flowResourceTypes.includes(flowResourceType) &&
        component !== LayoutComponentNames.SECTION
      );
    });
  }, [flowResourceType, getAllSettings]);

  const actions: IRightDrawerAction[] = useMemo(() => {
    return [
      {
        icon: isHidden ? VisibilityOffIcon : VisibilityIcon,
        onClick: onHideChange,
        hidden: !showHideAction,
        disabled,
        tooltip: isHidden ? t('actions.showComponent') : t('actions.hideComponent'),
      },
      {
        icon: ControlPointDuplicateRoundedIcon,
        onClick: onDuplicate,
        disabled,
        tooltip: t('actions.duplicate'),
      },
      {
        icon: DeleteOutlinedIcon,
        onClick: onDelete,
        disabled,
        tooltip: isSection ? t('tooltips.deleteSection') : t('tooltips.deleteComponent'),
      },
    ];
  }, [isHidden, isSection, disabled, showHideAction, onDuplicate, onDelete, onHideChange, t]);

  const header =
    typeof title === 'string' ? (
      <Typography sx={{ textTransform: 'none', color: palette.text.primary, fontWeight: 500 }}>
        {title}
      </Typography>
    ) : (
      title
    );

  return (
    <>
      <ConfigureDrawerHeader isChat={!isConfigurationTab} gap={0.5}>
        {!isSubEditor && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <RightDrawerHeaderSwitcher />
            {isConfigurationTab && !isPageLoading ? <RightDrawerActions actions={actions} /> : null}
          </Stack>
        )}

        <Stack direction="row" alignItems="center" spacing={1} minHeight={36}>
          {onClose && CloseIcon ? (
            <IconButton onClick={onClose} sx={{ width: 32, height: 32 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}

          {isConfigurationTab && !isSubEditor ? (
            <Tooltip title={isSection ? undefined : t('labels.changeComponent')}>
              <Button
                onClick={(e) => setChangeTypeAnchorEl(e.currentTarget)}
                color="secondary"
                disabled={isSection || isChanging}
                sx={{
                  width: 'auto',
                  minWidth: 'auto',
                  alignSelf: 'flex-start',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1.5,
                  '&:hover': { backgroundColor: palette.action.hover },
                }}
                startIcon={isChanging && <CircularProgress size="1em" />}
              >
                {header}
                {!isSection ? (
                  <KeyboardArrowDownRoundedIcon sx={{ width: 20, height: 20 }} />
                ) : null}
              </Button>
            </Tooltip>
          ) : (
            header
          )}
        </Stack>
      </ConfigureDrawerHeader>
      <Popover
        open={!!changeTypeAnchorEl}
        onClose={() => setChangeTypeAnchorEl(null)}
        anchorEl={changeTypeAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List sx={{ width: 180, p: 0, px: 0.5, py: 0 }}>
          {interchangeableComponents.map(([component, componentConfig], index) => {
            const isSelected = settings?.displayName === componentConfig.displayName;

            return (
              <ListItem
                key={`inter-changable-component-${index}`}
                sx={{ py: 1, px: 0.5, borderRadius: '4px', my: 0.5 }}
                button
                selected={isSelected}
                onClick={() => {
                  if (!isSelected) {
                    changeType(component, componentConfig);
                  }
                }}
              >
                <ListItemText sx={{ m: 0 }} primary={componentConfig.displayName} />
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export const LayoutComponentEditorHeader = (props: ILayoutComponentEditorProps) => {
  const { layoutId, value, onChange } = props;

  const { getSettings } = useLayoutComponentSettings();
  const subEditorProps = useSubEditorProps(props);

  const subEditor = useLayoutComponentEditorStore((s) => s.getSubEditor(), isEqual);
  const closeSubEditor = useLayoutComponentEditorStore((s) => s.closeSubEditor);
  const resetSubEditors = useLayoutComponentEditorStore((s) => s.resetSubEditors);

  const settings = useMemo(() => getSettings(value), [value, getSettings]);
  const componentHash = useMemo(() => {
    return hash(JSON.stringify([value.id, value.component]));
  }, [value.id, value.component]);

  // reset when component changed
  useEffect(() => {
    resetSubEditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentHash]);

  return (
    <RenderDynamicHook hook={subEditor?.useSubEditor} args={subEditorProps}>
      {(editor) =>
        editor && subEditor ? (
          <Header
            layoutId={layoutId}
            value={value}
            title={editor.header}
            onClose={closeSubEditor}
            CloseIcon={ArrowBackIcon}
            onChange={onChange}
            isSubEditor
          />
        ) : (
          <Header
            layoutId={layoutId}
            value={value}
            title={settings?.displayName ?? value.component}
            onChange={onChange}
          />
        )
      }
    </RenderDynamicHook>
  );
};
